import React from "react";

// "Open Ring" mark — approved logo concept 3. An open ring in paper-50
// (currentColor) on an indigo-600 badge, with a small solid rose-700 accent
// dot in the gap. Reads as "attentive/listening," not a letterform.
export const AppIcon = () => (
  <div className="h-12 w-12 rounded-control grid place-items-center bg-indigo-600 text-paper-50">
    <svg className="h-8 w-8" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M20.38,37.52 A14,14 0 1 1 37.52,27.62" stroke="currentColor" strokeWidth="5.5" strokeLinecap="round" />
      <circle cx="31.5" cy="33.5" r="4.4" fill="#9E4F60" />
    </svg>
  </div>
);

export const UserIcon = () => (
  <svg className="w-6 h-6 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

export const MicIcon = ({ size = 32, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden className={className}>
    <rect x="9" y="2" width="6" height="13" rx="3" stroke="currentColor" strokeWidth="2" />
    <path d="M5 11a7 7 0 0 0 14 0" stroke="currentColor" strokeWidth="2" />
    <path d="M12 19v3" stroke="currentColor" strokeWidth="2" />
  </svg>
);

export const StopIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

export const BookIcon = ({ className = "" }) => (
  <svg className={`w-8 h-8 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v15H6.5A2.5 2.5 0 0 1 4 14.5v-10A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);

export const ChartIcon = ({ className = "" }) => (
  <svg className={`w-8 h-8 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20V10"/>
    <path d="M18 20V4"/>
    <path d="M6 20v-4"/>
  </svg>
);

export const AdminIcon = ({ className = "" }) => (
  <svg className={`w-8 h-8 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

export const CheckIcon = () => (
  <svg className="w-6 h-6 text-success-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

export const NextStepIcon = () => (
  <svg className="w-6 h-6 text-warning-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

export const XIcon = ({ className = "w-6 h-6 text-danger-600 flex-shrink-0" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 6l12 12M18 6L6 18" />
  </svg>
);

// ---- Admin dashboard export-card icons -------------------------------
// Hand-authored inline SVGs (same pattern as the rest of this file) used
// to replace the 📊/🎯/💬 emoji-as-icons previously used as labels on the
// Research Data Export buttons in AdminView.js. ChartIcon above already
// covers the "discourse analysis" bar-chart glyph.

export const TargetIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
  </svg>
);

export const ChatIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-4 4v-4H6a2 2 0 0 1-2-2V5z" />
  </svg>
);

// ---- Conversation Starters topic icons -------------------------------
// Hand-authored inline SVGs, matching the existing icon pattern in this
// file (no icon library dependency — package.json doesn't have one, and
// the brief asked to flag before adding a new dependency rather than add
// one silently). Each takes a `className` for sizing/color so the caller
// can drop it into a tinted circle badge.
//
// These six map to the six topics Bernardo asked to bring back (Ordering
// Coffee, Talking About Hobbies, Daily Routine, Travel Plans, Food &
// Cooking, Weekend Activities) — content reverted to the pre-Wine&Stone
// set, but drawn as proper line icons for the circle-badge treatment
// instead of the old flat/oversized emoji they replace.

export const CoffeeIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 9h13v5a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V9z" />
    <path d="M17 10.5h1.5a2.5 2.5 0 0 1 0 5H17" />
    <path d="M8 3c0 1-1 1-1 2s1 1 1 2" />
    <path d="M12 3c0 1-1 1-1 2s1 1 1 2" />
  </svg>
);

export const HobbiesIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l2.2 5 5.3.5-4 3.6 1.2 5.2L12 14.9l-4.7 2.4 1.2-5.2-4-3.6 5.3-.5L12 3z" />
  </svg>
);

export const ClockIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </svg>
);

export const PlaneIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 11l18-8-8 18-2-8-8-2z" />
  </svg>
);

export const SkilletIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="10" cy="14" r="6" />
    <path d="M16 14h5" />
    <path d="M8.5 7.5c0-1 .5-1.8 1-2.5M12.5 7.5c0-1-.5-1.8-1-2.5" />
  </svg>
);

export const PartyIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2v4M12 18v4M2 12h4M18 12h4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8" />
  </svg>
);
